import React, { useState } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';

export default function DriverSettings() {
  const { userProfile, refreshProfile, logout } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState(userProfile?.phone || '');
  const [savingPhone, setSavingPhone] = useState(false);
  const [autoAccept, setAutoAccept] = useState(userProfile?.autoAcceptOffers === true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const handleSavePhone = async () => {
    if (!phone || phone.trim().length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }
    if (phone === userProfile?.phone) return;
    setSavingPhone(true);
    try {
      await updateDoc(doc(db, 'users', userProfile.id), {
        phone: phone.trim(),
        phoneVerified: false,
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success('Phone number updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
    setSavingPhone(false);
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      await updateDoc(doc(db, 'users', userProfile.id), {
        autoAcceptOffers: autoAccept,
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
    setSavingPrefs(false);
  };

  const handleSignOut = async () => {
    try { await logout(); navigate('/login'); } catch (err) { toast.error('Logout failed'); }
  };

  const tier = userProfile?.driverStats?.adminOverrideTier || userProfile?.driverStats?.qualityTier || '—';

  return (
    <div className="max-w-[600px] mx-auto px-6 py-8">
      <Link to="/driver" className="text-brand-primary font-semibold text-[0.9rem] mb-6 block">← Back</Link>
      <h1 className="font-serif text-[1.8rem] mb-6">Settings</h1>

      {/* Profile + Driver Stats */}
      <Section title="Driver Profile">
        <ReadOnlyField label="Name" value={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`} />
        <ReadOnlyField label="Email" value={userProfile?.email} />
        <ReadOnlyField label="Driver ID" value={userProfile?.referenceNumber} />
        <ReadOnlyField label="Quality Tier" value={tier.toString().toUpperCase()} />
        <ReadOnlyField label="Average Rating" value={`★ ${(userProfile?.driverStats?.adjustedRating || userProfile?.averageRating || 0).toFixed(1)}`} />
        <ReadOnlyField label="Completed Trips" value={userProfile?.completedTrips || 0} />
      </Section>

      {/* Phone */}
      <Section title="Phone Number">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-[0.85rem] font-semibold text-body mb-1 block">Phone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem]" />
          </div>
          <button onClick={handleSavePhone} disabled={savingPhone || phone === userProfile?.phone} className="px-[18px] py-2.5 rounded-[10px] border-0 text-white font-semibold"
            style={{
              background: phone !== userProfile?.phone ? '#2dd4a8' : '#e2e8f0',
              cursor: phone !== userProfile?.phone ? 'pointer' : 'not-allowed',
            }}>{savingPhone ? 'Saving...' : 'Save'}</button>
        </div>
      </Section>

      {/* Driving preferences */}
      <Section title="Driving Preferences">
        <Toggle label="Auto-accept targeted offers"
          desc="When riders pre-select you, automatically accept their offers (within 60s) instead of showing a confirmation prompt"
          value={autoAccept} onChange={setAutoAccept} />
        <button onClick={handleSavePrefs} disabled={savingPrefs} className="mt-3 px-[18px] py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold">{savingPrefs ? 'Saving...' : 'Save Preferences'}</button>
      </Section>

      {/* Referral code */}
      <Section title="Your Referral Code">
        {userProfile?.referralCode ? (
          <>
            <p className="text-[0.85rem] text-body mb-3">
              Share this code with new riders. When they sign up using it and complete their first trip,
              you earn <strong>$5 + 5 reward points</strong>.
            </p>
            <div className="flex gap-2 items-center mb-2">
              <code className="flex-1 px-4 py-3 rounded-[10px] bg-slate-50 border-[1.5px] border-slate-200 text-[1.2rem] font-bold font-mono text-center text-brand-dark" style={{ letterSpacing: '0.15em' }}>{userProfile.referralCode}</code>
              <button onClick={() => {
                navigator.clipboard.writeText(userProfile.referralCode);
                toast.success('Code copied');
              }} className="px-4 py-3 rounded-[10px] border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold text-[0.85rem] whitespace-nowrap">Copy</button>
              <button onClick={() => {
                const text = `Join Reservo with my referral code: ${userProfile.referralCode}`;
                if (navigator.share) {
                  navigator.share({ title: 'Reservo referral', text }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(text);
                  toast.success('Share text copied');
                }
              }} className="px-4 py-3 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-brand-dark cursor-pointer font-semibold text-[0.85rem] whitespace-nowrap">Share</button>
            </div>
            {(userProfile?.lifetimeReferralCount > 0) && (
              <div className="text-[0.78rem] text-muted mt-2">
                Lifetime referrals: <strong className="text-brand-dark">{userProfile.lifetimeReferralCount}</strong> ·
                Earned: <strong className="text-brand-dark">${(userProfile.lifetimeReferralBonusAUD || 0).toFixed(2)}</strong>
              </div>
            )}
          </>
        ) : (
          <p className="text-[0.85rem] text-muted">
            Your referral code will appear here once your application is approved.
          </p>
        )}
      </Section>

      {/* Security */}
      <Section title="Security">
        <p className="text-[0.85rem] text-body mb-3">Trip PIN entry uses your security PIN.</p>
        <Link to="/set-pin" className="inline-block px-[18px] py-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-brand-dark no-underline font-semibold text-[0.9rem]">Change PIN</Link>
      </Section>


      {/* Payouts */}
      <Section title="Payouts">
        <p className="text-[0.85rem] text-body mb-3">
          Your earnings are paid out via Stripe Connect. To manage payout methods, visit Stripe directly.
        </p>
        <button onClick={() => toast('Stripe Connect dashboard coming soon', { icon: 'ℹ️' })} className="px-[18px] py-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-brand-dark cursor-pointer font-semibold text-[0.9rem]">Stripe Dashboard</button>
      </Section>

      {/* Legal */}
      <Section title="Legal">
        <Link to="/legal" className="text-brand-primary font-semibold text-[0.9rem]">Terms & Privacy →</Link>
      </Section>

      <button onClick={handleSignOut} className="mt-5 w-full px-[18px] py-3 rounded-[10px] border-[1.5px] bg-white cursor-pointer font-bold"
        style={{ borderColor: '#dc2626', color: '#dc2626' }}>Sign Out</button>
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5 p-5 bg-white rounded-2xl border border-slate-200">
      <h3 className="mt-0 mb-3 text-[1rem] text-brand-dark">{title}</h3>
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 text-[0.9rem]">
      <span className="text-body">{label}</span>
      <span className="font-medium">{value || '—'}</span>
    </div>
  );
}

function Toggle({ label, value, onChange, desc }) {
  return (
    <div className="flex justify-between items-start py-2.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
      <div className="flex-1">
        <div className="font-semibold text-[0.9rem]">{label}</div>
        {desc && <div className="text-[0.78rem] text-muted mt-0.5">{desc}</div>}
      </div>
      <button onClick={() => onChange(!value)} className="w-11 h-6 rounded-xl border-0 cursor-pointer relative ml-3 transition-all"
        style={{ background: value ? '#2dd4a8' : '#e2e8f0' }}>
        <span className="absolute top-0.5 w-5 h-5 rounded-full transition-all" style={{ left: value ? 22 : 2, background: '#fff' }} />
      </button>
    </div>
  );
}